import { useState, useEffect, useMemo, useRef } from 'react';
import { Pagination, Avatar, LoadingSpinner, EmptyState } from '../../index';
import { GroupAvatar } from '../avatar/GroupAvatar';
import { 
  Search, 
  User,
  ArrowRight,
  Book,
  Calendar,
  Tag,
  Workflow,
  SearchSlash,
  BookTemplate
} from 'lucide-react';
import { Button } from '../button/Button';

// Export types for use in consuming apps
export interface SearchResult {
  id: string;
  type: string;
  title: string;
  description: string;
  excerpt?: string;
  url: string;
  lastModified: string | Date;
  tags?: string[];
  relevance?: number;
  owner?: string;
  author?: string;
  organization?: string;
  coverImage?: string;
  avatar?: string;
  profilePhoto?: string;
}

/**
 * Result types whose image is a person's photo rather than an organization's
 * logo. Two decisions read this and must not drift apart: the row renders a
 * person in a round `Avatar` and a logo in a square tile, and a consuming app
 * resolves the file against auth for a person and against its own service for a
 * logo (dashboard's `searchService`). Adding a type to one list only would
 * render a round frame pointed at the wrong host — a 404 with no error anywhere.
 */
export const PERSON_RESULT_TYPES = ['person', 'member', 'user'] as const;

export function isPersonResultType(type: string | undefined): boolean {
  return !!type && (PERSON_RESULT_TYPES as readonly string[]).includes(type);
}

export interface SearchFilter {
  value: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

export interface SearchResultsConfig {
  /**
   * Heading rendered above the filter row. Omit it when the page supplies its
   * own heading (e.g. `PageHeader`), or the page gets two competing `h1`s.
   */
  title?: string;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  clearSearchUrl?: string;
  itemsPerPage?: number;
  showGroupedResults?: boolean;
}

interface SearchResultsProps {
  results: SearchResult[];
  filters: SearchFilter[];
  /**
   * The IANA zone a result's date is read in. Omitted means the browser's,
   * which is what this rendered before anyone could choose one — a consumer
   * whose app honours a timezone preference should pass it, or its search
   * results are the one surface still showing browser-local dates.
   */
  timezone?: string | null;
  onResultClick: (result: SearchResult) => void;
  onSearch?: (query: string) => void;
  onFilterChange?: (filter: string) => void;
  onClearFilters?: () => void;
  isLoading?: boolean;
  config?: SearchResultsConfig;
  initialQuery?: string;
  initialFilter?: string;
  currentPage?: number;
  totalPages?: number;
  totalResults?: number;
  onPageChange?: (page: number) => void;
  /**
   * Rendered in place of the results region (spinner, results, pagination)
   * when the page's own search failed. The heading and the filter row stay
   * put, so a failed search still leaves the operator able to widen or clear
   * the filter instead of only retrying the query that just failed.
   *
   * A rendered node rather than `DataTable`'s `error` + `onRetry` pair, on
   * purpose: this package carries no i18n, so `DataTable` ends up rendering an
   * English "Try Again" at any locale. Taking the node lets each app pass its
   * own translated error block.
   */
  errorState?: React.ReactNode;
}

type SlotIcon = React.ComponentType<{ className?: string }>;

/**
 * The square frame a result's own icon sits in when it has no image.
 *
 * The dark foreground steps up to `--primary-hover`: on a `dark:bg-primary/20`
 * tint, `text-primary` lands below AA contrast (CLAUDE.md, brand tokens).
 */
const IconTile: React.FC<{ icon: SlotIcon }> = ({ icon: Icon }) => (
  <div className="w-full h-full rounded-lg bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
    <Icon className="h-6 w-6 text-primary dark:text-[hsl(var(--primary-hover))]" />
  </div>
);

/**
 * Whether a result's picture is still worth rendering, and how to record that it
 * isn't.
 *
 * Two things this deliberately is not. It is not a DOM mutation — hiding the
 * `<img>` by setting `style.display` from an `onError` handler takes the element
 * out from under React, which never manages that property again, so a replaced
 * picture loads into a node that stays hidden for the life of the mount. A
 * result keeps its `key` across searches, so that is reachable any time an image
 * is swapped: exactly the bug this component's `Avatar` had. And it is not a
 * bare "it failed" flag, which only recovers if every caller remembers
 * `key={src}`. Remembering the *address* that failed needs nothing of the
 * caller and gives each new address its own chance.
 */
function useImageFallback(src: string | undefined) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  return {
    showImage: !!src && src !== failedSrc,
    onError: () => {
      if (src) setFailedSrc(src);
    },
  };
}

/**
 * An organization's logo — square, `object-contain`, so a wordmark isn't cropped.
 */
const LogoTile: React.FC<{ src: string; icon: SlotIcon }> = ({ src, icon }) => {
  const image = useImageFallback(src);

  // A broken-image glyph would be worse than the empty box this replaced.
  if (!image.showImage) return <IconTile icon={icon} />;

  return (
    <div className="w-full h-full rounded-lg overflow-hidden shadow-sm bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
      {/* Decorative: the row's own heading names this result, and an alt text
          repeating it just gets announced twice. */}
      <img
        src={src}
        alt=""
        className="w-full h-full object-contain"
        onError={image.onError}
      />
    </div>
  );
};

/**
 * The wide cover a playbook, template or training flow carries, on the gradient
 * ground those results have always used. Falls back to the type's own glyph when
 * there is no cover or the cover fails.
 */
const CoverTile: React.FC<{ src?: string; alt: string; icon: SlotIcon }> = ({ src, alt, icon: Icon }) => {
  const image = useImageFallback(src);

  return (
    <div className="w-full h-full rounded-lg overflow-hidden shadow-sm bg-gradient-to-br from-primary/10 to-primary/20 dark:from-primary/15 dark:to-primary/25 flex items-center justify-center">
      {image.showImage ? (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          onError={image.onError}
        />
      ) : (
        <Icon className="h-6 w-6 text-primary" />
      )}
    </div>
  );
};

// Component for rendering a section of results
interface ResultSectionProps {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  results: SearchResult[];
  onResultClick: (result: SearchResult) => void;
  formatDate: (date: string | Date) => string;
  totalResults?: number;
  /**
   * The page's own filter list, which already pairs every result type with an
   * icon. Reusing it is what keeps the row's visual slot filled for types this
   * component has never heard of — dashboard's `member`/`company`/`workspace`
   * matched none of the hardcoded branches below and rendered an empty 64x48
   * box on every row, discarding the member photos and workspace logos the API
   * was already sending.
   */
  filters: SearchFilter[];
}

const ResultSection: React.FC<ResultSectionProps> = ({ 
  title,
  icon: SectionIcon,
  results,
  onResultClick,
  formatDate,
  totalResults,
  filters
}) => {
  if (results.length === 0) return null;

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center gap-3 pb-2 border-b border-gray-200 dark:border-gray-700">
        <div className="w-6 h-6 bg-primary/15 dark:bg-primary/25 rounded-md flex items-center justify-center">
          <SectionIcon className="h-3.5 w-3.5 text-primary" />
        </div>
        <h2 className="text-xl font-semibold text-foreground">{title}</h2>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {totalResults || results.length}
        </span>
      </div>

      {/* Results List */}
      <div className="space-y-0">
        {results.map((result) => {
          // `SearchSlash` for a type the page exposes no filter for — the grouped
          // section header already falls back this way, and without it such a
          // result lands on `slot === 'none'`: the empty 64x48 box this slot
          // exists to fill. `getResultTypeLabel` already knows four types that
          // neither consumer filters on.
          const TypeIcon = filters.find((f) => f.value === result.type)?.icon ?? SearchSlash;
          const image = result.avatar || result.profilePhoto;
          // A round frame reads as a face and a square one as a logo, so a
          // person's photo goes in an `Avatar` while a workspace or company
          // image goes in a rounded-lg tile with `object-contain` — the way the
          // rest of the suite renders logos (dashboard's `WorkspaceSettingsTab`),
          // and because a wordmark cropped into a circle looks broken.
          const isCoverType =
            result.type === 'playbook' || result.type === 'training-flow' || result.type === 'template';
          // One slot, one occupant — picked here so the branches below can't
          // overlap as new types arrive. `image` deliberately outranks `group`:
          // a group that carries a picture shows the picture (square — a group
          // is not a face), and `GroupAvatar`'s initials are the fallback for one
          // that has none.
          const slot: 'cover' | 'person' | 'logo' | 'group' | 'icon' = isCoverType
            ? 'cover'
            : isPersonResultType(result.type)
              ? 'person'
              : image
                ? 'logo'
                : result.type === 'group'
                  ? 'group'
                  : 'icon';
          return (
            <div
              key={result.id}
              onClick={() => onResultClick(result)}
              className="py-3 cursor-pointer group hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-lg px-3 -mx-3 transition-colors"
            >
              <div className="flex items-start gap-3">
                {/* Visual Element - Cover Image for Playbooks/Training Flows, Avatar for People, Icon for Groups */}
                <div className="flex-shrink-0 w-16 h-12 flex items-center justify-center">
                  {slot === 'person' && (
                    <Avatar
                      name={result.title}
                      src={image}
                      size="lg"
                      className="shadow-sm"
                    />
                  )}
                  {slot === 'logo' && image && (
                    <LogoTile src={image} icon={TypeIcon} />
                  )}
                  {slot === 'group' && (
                    <GroupAvatar
                      name={result.title}
                      size="md"
                      variant="blue"
                      className="shadow-sm"
                    />
                  )}
                  {slot === 'cover' && (
                    <CoverTile
                      src={result.coverImage}
                      alt={result.title}
                      // The cover types keep their own glyphs rather than the
                      // filter's: playbook's `training-flow` filter is commented
                      // out, so `TypeIcon` would hand it `SearchSlash`.
                      icon={
                        result.type === 'playbook'
                          ? Book
                          : result.type === 'template'
                            ? BookTemplate
                            : Workflow
                      }
                    />
                  )}
                  {slot === 'icon' && <IconTile icon={TypeIcon} />}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base font-medium text-primary truncate">
                      {result.title}
                    </h3>
                  </div>
                  {result.excerpt && result.excerpt !== result.description ? (
                    <p className="text-gray-500 dark:text-gray-500 text-sm mb-2 line-clamp-2">
                      {result.excerpt}
                    </p>
                  ) : (
                    result.description && (
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">
                        {result.description}
                      </p>
                    )
                  )}
                  <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-500">
                    {(result.owner || result.author) && result.type !== 'template' && (
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {result.owner || result.author}
                      </span>
                    )}
                    {result.lastModified && result.type !== 'template' && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(result.lastModified)}
                      </span>
                    )}
                    {result.tags && result.tags.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Tag className="h-3 w-3" />
                        <div className="flex gap-1">
                          {result.tags.slice(0, 3).map((tag, index) => (
                            <span key={`${index}-${tag}`} className="text-primary">
                              #{tag}
                            </span>
                          ))}
                          {result.tags.length > 3 && (
                            <span>+{result.tags.length - 3} more</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0 mt-1">
                  <ArrowRight className="h-4 w-4 text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SearchResults: React.FC<SearchResultsProps> = ({
  timezone,
  results,
  filters,
  onResultClick,
  onSearch,
  onFilterChange,
  onClearFilters,
  isLoading = false,
  config = {},
  initialQuery = '',
  initialFilter = 'all',
  currentPage: externalCurrentPage,
  totalPages: externalTotalPages,
  totalResults: externalTotalResults,
  onPageChange,
  errorState
}) => {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [selectedFilter, setSelectedFilter] = useState(initialFilter);
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use external pagination if provided, otherwise use internal
  const currentPage = externalCurrentPage !== undefined ? externalCurrentPage : internalCurrentPage;
  const totalPages = externalTotalPages || Math.ceil(results.length / (config.itemsPerPage || 10));
  const totalResults = externalTotalResults !== undefined ? externalTotalResults : results.length;

  const {
    title,
    emptyStateTitle = 'No results found',
    emptyStateDescription = 'Try adjusting your search terms or filters.',
    itemsPerPage = 10,
    showGroupedResults = true
  } = config;

  // Handle initial values from props
  useEffect(() => {
    setSearchQuery(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    setSelectedFilter(initialFilter);
  }, [initialFilter]);

  // Filter results based on search query and selected filter
  // Only do local filtering if parent callbacks are not provided
  const filteredResults = useMemo(() => {
    // If parent is managing search/filter via callbacks, don't filter locally
    if (onSearch || onFilterChange) {
      return results;
    }

    let filteredData = results;

    // Filter by type
    if (selectedFilter !== 'all') {
      filteredData = filteredData.filter(result => result.type === selectedFilter);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredData = filteredData.filter(result =>
        result.title.toLowerCase().includes(query) ||
        result.description.toLowerCase().includes(query) ||
        result.excerpt?.toLowerCase().includes(query) ||
        result.tags?.some(tag => tag.toLowerCase().includes(query)) ||
        (result.owner || result.author)?.toLowerCase().includes(query)
      );
    }

    return filteredData;
  }, [results, selectedFilter, searchQuery, onSearch, onFilterChange]);

  // Helper functions
  // removed getResultIcon as it was unused

  const getResultTypeLabel = (type: string) => {
    switch (type) {
      case 'training-flow': return 'Training Flow';
      case 'playbook': return 'Playbook';
      case 'template': return 'Template';
      case 'person': return 'Person';
      case 'organization': return 'Organization';
      case 'setting': return 'Setting';
      case 'report': return 'Report';
      case 'member': return 'Member';
      case 'dashboard': return 'Dashboard';
      case 'invoice': return 'Invoice';
      default: return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const formatDate = (date: string | Date) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
      ...(timezone ? { timeZone: timezone } : {})
    });
  };

  // Handle filter changes
  const handleFilterSelect = (filterValue: string) => {
    setSelectedFilter(filterValue);
    if (onFilterChange) {
      // A consumer that owns filtering also owns the page reset — both in-repo
      // pages drop `?page=` in this handler. Calling `onPageChange(1)` first
      // made one filter click write the URL twice, and the intermediate entry
      // was the *old* filter at page 1: a state the operator never visited, but
      // one that Back returns to. The internal page still resets, for a
      // consumer that hands us filtering but leaves pagination in here.
      setInternalCurrentPage(1);
      onFilterChange(filterValue);
      return;
    }
    // Reset to page 1 when filter changes
    if (onPageChange) {
      onPageChange(1);
    } else {
      setInternalCurrentPage(1);
    }
  };

  // Handle page changes
  const handlePageChange = (page: number) => {
    if (onPageChange) {
      onPageChange(page);
    } else {
      setInternalCurrentPage(page);
    }
  };

  // Group results by type for grouped display
  const groupedResults = useMemo(() => {
    if (!showGroupedResults) return {};

    return filteredResults.reduce((groups, result) => {
      const type = result.type;
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(result);
      return groups;
    }, {} as Record<string, SearchResult[]>);
  }, [filteredResults, showGroupedResults]);

  // Pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedResults = filteredResults.slice(startIndex, endIndex);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="w-full space-y-10">
      <div className="w-full space-y-4">
        {/* Header (omitted when the page renders its own) */}
        {title && (
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          {filters.map((filter) => {
            const Icon = filter.icon;
            const isActive = selectedFilter === filter.value;
            
            return (
              <Button
                key={filter.value}
                variant={isActive ? "default" : "secondary"}
                size="sm"
                onClick={() => handleFilterSelect(filter.value)}
                className="flex items-center gap-2"
              >
                <Icon className="h-4 w-4" />
                {filter.label}
              </Button>
            );
          })}
        </div>
      </div>

      {errorState}

      {/* Loading State */}
      {!errorState && isLoading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Results */}
      {!errorState && !isLoading && (
        <>
          {filteredResults.length === 0 ? (
            <EmptyState
              icon={Search}
              title={emptyStateTitle}
              description={emptyStateDescription}
              action={
                searchQuery || selectedFilter !== 'all' ? (
                  <Button 
                    variant="outline"
                    onClick={() => {
                      if (onClearFilters) {
                        // Let parent handle clearing (including navigation)
                        onClearFilters();
                      } else {
                        // Fallback to local clearing
                        setSearchQuery('');
                        setSelectedFilter('all');
                        if (onPageChange) {
                          onPageChange(1);
                        } else {
                          setInternalCurrentPage(1);
                        }
                        if (onSearch) onSearch('');
                        if (onFilterChange) onFilterChange('all');
                      }
                    }}
                  >
                    Clear filters
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="space-y-8">
              {/* Show grouped results if enabled, otherwise show paginated results */}
              {showGroupedResults ? (
                <div className="space-y-8">
                  {Object.entries(groupedResults).map(([type, typeResults]) => {
                    const filter = filters.find(f => f.value === type);
                    const icon = filter?.icon || SearchSlash;
                    const label = filter?.label || getResultTypeLabel(type);
                    
                    return (
                      <ResultSection
                        key={type}
                        title={label}
                        icon={icon}
                        results={typeResults}
                        onResultClick={onResultClick}
                        formatDate={formatDate}
                        totalResults={filteredResults.length}
                        filters={filters}
                      />
                    );
                  })}
                </div>
              ) : (
                <ResultSection
                  title="Results"
                  icon={Search}
                  results={onPageChange ? filteredResults : paginatedResults}
                  onResultClick={onResultClick}
                  formatDate={formatDate}
                  totalResults={totalResults}
                  filters={filters}
                />
              )}
            </div>
          )}

          {/* Pagination */}
          {!showGroupedResults && totalPages > 1 && (
            <div className="flex justify-center">
              <Pagination
                currentPage={currentPage}
                totalItems={totalResults}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export { SearchResults };
export default SearchResults;
